import {
  type FetchServiceProviderListParams,
  fetchServiceProviders,
  fetchSingleServiceProvider,
  type ServiceProviderType
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export const SELECTED_SERVICE_PROVIDER_PARAMETER_KEY = 'selectedServiceProvider'

function useServiceProvidersWithParams() {
  const dispatch = useAppDispatch()
  const { serviceProviders, loading, total_results, next_page, previous_page } = useTypedSelector(
    state => state.serviceProvider
  )
  const [searchParams, setSearchParams] = useSearchParams()

  const [params, setParams] = useState<FetchServiceProviderListParams>({
    active:
      searchParams.get('active') === 'true'
        ? true
        : searchParams.get('active') === 'false'
          ? false
          : undefined,
    name: searchParams.get('name') ?? undefined,
    search_text: searchParams.get('search_text') ?? undefined,
    type: (searchParams.get('type') as ServiceProviderType) ?? undefined,
    sort_by: searchParams.get('sort_by') ?? 'serviceProviderId',
    sort_order: searchParams.get('sort_order') ?? SORT_ORDER_ASC,
    start_index: searchParams.get('start_index') ?? undefined,
    count: ROWS_PER_PAGE_DEFAULT
  })

  const selectedServiceProviderQueryParam =
    searchParams.get(SELECTED_SERVICE_PROVIDER_PARAMETER_KEY) ?? undefined
  const [selectedServiceProviderId, setSelectedServiceProviderId] = useState(
    selectedServiceProviderQueryParam
  )

  const setQueryParams = () => {
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value.toString())
      } else {
        searchParams.delete(key)
      }
    })

    selectedServiceProviderId &&
      searchParams.set(SELECTED_SERVICE_PROVIDER_PARAMETER_KEY, selectedServiceProviderId)

    setSearchParams(searchParams)
  }

  useEffect(() => {
    dispatch(fetchServiceProviders(params))
    setQueryParams()
  }, [params])

  useEffect(() => {
    if (selectedServiceProviderId) {
      dispatch(fetchSingleServiceProvider({ id: selectedServiceProviderId }))
      setQueryParams()
    } else {
      searchParams.delete(SELECTED_SERVICE_PROVIDER_PARAMETER_KEY)
      setSearchParams(searchParams)
    }
  }, [selectedServiceProviderId])

  const fetchServiceProvidersWithParams = (params: FetchServiceProviderListParams) => {
    dispatch(fetchServiceProviders(params))
    setQueryParams()
  }

  return {
    serviceProviders,
    loading,
    params,
    setParams,
    total_results,
    next_page,
    previous_page,
    selectedServiceProviderId,
    setSelectedServiceProviderId,
    fetchServiceProvidersWithParams
  }
}

export default useServiceProvidersWithParams
